import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, finalize, map, of, startWith } from 'rxjs';

import { PlayerCardComponent } from '../../../../community/components/player-card/player-card.component';
import type { PublicGameSectionSummary } from '../../../../game-section/data/game-section.model';
import { GameSectionService } from '../../../../game-section/data/game-section.service';
import { PlayersResendVerificationComponent } from '../../../../../shared/players/players-resend-verification.component';
import {
  PLAYER_PROVINCES,
  PLAYERS_PRIVACY_FRAGMENT,
  PLAYERS_PRIVACY_PATH,
} from '../../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../../shared/players/players.copy';
import type {
  OwnPlayerProfile,
  OwnPlayerProfileInput,
  PlayerCard,
  PlayerContactMethod,
  PlayerGame,
} from '../../../../../shared/players/players.model';
import { PlayersService } from '../../../../../shared/players/players.service';
import { isVerifiedAccount } from '../../../../../shared/players/players.utils';
import { AuthService } from '../../../../../shared/services/auth.service';
import { LocationsService } from '../../../../../shared/services/locations.service';

/** Claves del 422 de `PUT /players/me` que tienen sitio bajo un campo (§5.6.5). */
export const PROFILE_FIELD_KEYS = [
  'alias',
  'province',
  'canton',
  'games',
  'contact_method',
  'contact_value',
  'bio',
  'consent',
  'looking_for_players',
] as const;
export type ProfileFieldKey = (typeof PROFILE_FIELD_KEYS)[number];

export const PLAYER_BIO_MAX = 280;

const CONTACT_METHODS: readonly PlayerContactMethod[] = ['whatsapp', 'discord', 'email', 'tienda'];

/**
 * Formulario del perfil de jugador (J2, D18-D23, D25). La API valida; la web solo ayuda
 * y pinta su respuesta.
 *
 * - **Sustitución completa** (D18): el `PUT` lleva siempre todos los campos, con `null`
 *   donde no hay valor; `contact_value` es `null` con `email` y `tienda`.
 * - **Consentimiento** (D21): solo se pide si el perfil aún no lo tiene y se activa
 *   «Busco jugadores»; sin la casilla, Guardar queda bloqueado.
 * - **Sin verificar** (D22): el interruptor sale deshabilitado **y apagado**, y el `PUT`
 *   lleva `looking_for_players: false`: la API rechaza un `true` de una cuenta sin
 *   verificar aunque el perfil ya estuviera activo, y sin esto no se podría guardar nada
 *   (auditoría de Alma, MAYOR). Al volver a verificar se habilita apagado.
 * - **422 por clave** (D23): el texto del servidor bajo su campo; una clave desconocida va
 *   al aviso general.
 */
@Component({
  selector: 'app-player-profile-form',
  imports: [PlayerCardComponent, PlayersResendVerificationComponent, ReactiveFormsModule],
  templateUrl: './player-profile-form.component.html',
  styleUrl: './player-profile-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PlayerProfileFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly players = inject(PlayersService);
  private readonly locations = inject(LocationsService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  /** `null` = alta. */
  readonly profile = input<OwnPlayerProfile | null>(null);
  /** La pestaña lo bloquea con la confirmación de borrado abierta o borrando (H2). */
  readonly locked = input(false);
  readonly saved = output<OwnPlayerProfile>();
  /** `true` mientras hay un `PUT` en vuelo: la pestaña deshabilita «Borrar» (H2). */
  readonly busyChange = output<boolean>();
  private alive = true;

  protected readonly copy = PLAYERS_COPY.profile;
  protected readonly consentCopy = PLAYERS_COPY.consent;
  protected readonly storeContactText = PLAYERS_COPY.store.contactText;
  protected readonly contactMethods = CONTACT_METHODS;
  protected readonly bioMax = PLAYER_BIO_MAX;
  protected readonly privacyPath = PLAYERS_PRIVACY_PATH;
  protected readonly privacyFragment = PLAYERS_PRIVACY_FRAGMENT;

  protected readonly form = this.fb.nonNullable.group({
    alias: '',
    province: '',
    canton: '',
    contact_method: 'tienda' as PlayerContactMethod,
    contact_value: '',
    bio: '',
    email_notifications: true,
    looking_for_players: false,
    consent: false,
  });

  /** Slugs elegidos (píldoras, fuera del `FormGroup`). */
  protected readonly selectedGames = signal<readonly string[]>([]);

  protected readonly value = toSignal(
    this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.form.getRawValue()),
    ),
    { initialValue: this.form.getRawValue() },
  );

  protected readonly games = toSignal(
    inject(GameSectionService)
      .getGames()
      .pipe(catchError(() => of<PublicGameSectionSummary[]>([]))),
    { initialValue: [] as PublicGameSectionSummary[] },
  );

  protected readonly verified = computed(() => isVerifiedAccount(this.auth.currentUser()));
  protected readonly accountEmail = computed(() => this.auth.currentUser()?.email ?? '');

  /** Nombres de `cr.json`; si aún no está o falló, los de `PLAYER_PROVINCES` (mismos nombres). */
  protected readonly provinceNames = computed(() => {
    const loaded = this.locations.provinces();
    return loaded ? loaded.map((p) => p.name) : PLAYER_PROVINCES.map((p) => p.name);
  });

  /** Sin `cr.json` no hay cantones: el campo se oculta (D20). */
  protected readonly cantonNames = computed(() =>
    this.locations.loadError() ? [] : this.locations.cantonsOf(this.value().province).map((c) => c.name),
  );

  protected readonly needsConsent = computed(
    () => !this.profile()?.consented && this.value().looking_for_players,
  );

  protected readonly saving = signal(false);
  protected readonly fieldErrors = signal<Partial<Record<ProfileFieldKey, string>>>({});
  protected readonly generalError = signal<string | null>(null);
  protected readonly notice = signal<string | null>(null);

  protected readonly canSave = computed(
    () => !this.locked() && !this.saving() && (!this.needsConsent() || this.value().consent),
  );

  protected readonly bioCount = computed(() => this.value().bio.length);

  /** Vista previa (D25): lo que verían otros, con los valores del formulario. */
  protected readonly preview = computed<PlayerCard>(() => {
    const v = this.value();
    const byslug = new Map<string, PlayerGame>();
    for (const g of this.profile()?.games ?? []) byslug.set(g.slug, g);
    for (const g of this.games()) byslug.set(g.slug, { slug: g.slug, name: g.name, accent_color: g.accent_color });
    const province = PLAYER_PROVINCES.find((p) => p.name === v.province);
    return {
      alias: v.alias.trim() || this.copy.previewAliasPlaceholder,
      province: { slug: province?.slug ?? '', name: v.province },
      canton: v.canton || null,
      games: this.selectedGames()
        .map((slug) => byslug.get(slug))
        .filter((g): g is PlayerGame => g !== undefined),
      bio: v.bio.trim() || null,
      listed_since: this.profile()?.listed_since ?? '',
      request_state: 'none',
    };
  });

  constructor() {
    this.locations.ensureLoaded();
    this.destroyRef.onDestroy(() => (this.alive = false));

    // El formulario se rehace con cada perfil que llega (carga, guardado o borrado).
    effect(() => {
      const profile = this.profile();
      untracked(() => this.reset(profile));
    });

    // Sin verificar, «Busco jugadores» sale apagado y no se puede tocar (D22). Se apaga
    // emitiendo, para que la vista previa y `needsConsent` lo vean.
    effect(() => {
      const control = this.form.controls.looking_for_players;
      if (this.verified()) {
        control.enable({ emitEvent: false });
      } else {
        control.setValue(false, { emitEvent: true });
        control.disable({ emitEvent: false });
      }
    });
  }

  protected onAliasInput(): void {
    const control = this.form.controls.alias;
    const lower = control.value.toLowerCase();
    if (lower !== control.value) control.setValue(lower);
  }

  protected onProvinceChange(): void {
    this.form.controls.canton.setValue('');
  }

  protected toggleGame(slug: string): void {
    this.selectedGames.update((games) =>
      games.includes(slug) ? games.filter((g) => g !== slug) : [...games, slug],
    );
  }

  protected isSelected(slug: string): boolean {
    return this.selectedGames().includes(slug);
  }

  protected hasContactValue(method: PlayerContactMethod): boolean {
    return method === 'whatsapp' || method === 'discord';
  }

  /** Cuerpo entero del `PUT` (D18). */
  protected body(): OwnPlayerProfileInput {
    const v = this.form.getRawValue();
    return {
      alias: v.alias.trim().toLowerCase(),
      province: v.province,
      canton: v.canton || null,
      games: [...this.selectedGames()],
      contact_method: v.contact_method,
      contact_value: this.hasContactValue(v.contact_method) ? v.contact_value.trim() || null : null,
      bio: v.bio.trim() || null,
      email_notifications: v.email_notifications,
      looking_for_players: this.verified() && v.looking_for_players,
      consent: this.needsConsent() && v.consent,
    };
  }

  protected save(): void {
    if (!this.canSave()) return;
    // Guarda de identidad (H1): si al responder la cuenta ya es otra, ni `saved` ni errores.
    const account = this.auth.currentUser()?.id ?? null;
    const sameAccount = () => (this.auth.currentUser()?.id ?? null) === account;
    this.saving.set(true);
    this.busyChange.emit(true);
    this.fieldErrors.set({});
    this.generalError.set(null);
    this.notice.set(null);
    this.players
      .saveMe(this.body())
      .pipe(
        finalize(() => {
          this.saving.set(false);
          if (this.alive) this.busyChange.emit(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (profile) => {
          if (!sameAccount()) return;
          this.saved.emit(profile);
          this.notice.set(this.copy.saved);
        },
        error: (err: unknown) => {
          if (!sameAccount()) return;
          this.showErrors(err);
        },
      });
  }

  private showErrors(err: unknown): void {
    if (!(err instanceof HttpErrorResponse) || err.status !== 422) {
      this.generalError.set(this.copy.saveError);
      return;
    }
    const errors = (err.error as { error?: unknown } | null)?.error;
    const fields: Partial<Record<ProfileFieldKey, string>> = {};
    let general: string | null = null;
    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors as Record<string, unknown>)) {
        const first = Array.isArray(messages) ? messages[0] : messages;
        if (typeof first !== 'string' || first.trim() === '') continue;
        if ((PROFILE_FIELD_KEYS as readonly string[]).includes(key)) fields[key as ProfileFieldKey] = first;
        else general ??= first;
      }
    }
    this.fieldErrors.set(fields);
    if (general === null && Object.keys(fields).length === 0) general = this.copy.saveError;
    this.generalError.set(general);
  }

  private reset(profile: OwnPlayerProfile | null): void {
    this.form.reset({
      alias: profile?.alias ?? '',
      province: profile?.province.name ?? '',
      canton: profile?.canton ?? '',
      contact_method: profile?.contact_method ?? 'tienda',
      contact_value: profile?.contact_value ?? '',
      bio: profile?.bio ?? '',
      email_notifications: profile?.email_notifications ?? true,
      looking_for_players: this.verified() && (profile?.looking_for_players ?? false),
      consent: false,
    });
    this.selectedGames.set(profile?.games.map((g) => g.slug) ?? []);
    this.fieldErrors.set({});
    this.generalError.set(null);
  }
}
